/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { accessAPI, Titulo, Boton, tituloBase } from "../../shared";
import { Skeleton } from "primereact/skeleton";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import TarjetaDetallesSolicitud from "./TarjetaDetallesSolicitud";
import TarjetaProcesosVigentes from "./TarjetaProcesosVigentes";
import TarjetaEvaluacionSolicitud from "./TarjetaEvaluacionSolicitud";
import { useContextoAsistente } from "../asistente/ContextoAsistente";
export default function Solicitud() {
  const [loader, setLoader] = useState(true);
  const [solicitud, setSolicitud] = useState();
  const [loaderCerrar, setLoaderCerrar] = useState(false);
  const [loaderReabrir, setLoaderReabrir] = useState(false);
  // Cambia al crear un proceso, para que la card de evaluación se recalcule
  const [recargaEvaluacion, setRecargaEvaluacion] = useState(0);

  const toast = useRef(null);

  // Carga el id del solicitud de la URL
  const { solicitudId } = useParams();
  useContextoAsistente({
    tipo: "solicitud",
    id: solicitudId,
    nombre: solicitud?.nombrefantasia
  });

  // Cuando carga el componente va a buscar el solicitud
  useEffect(() => {
    // Tengo que agregar este if para que no vuelva a llamar a la API cuando cambia otro objeto del componente
    if (!solicitud) {
      accessAPI(
        "GET",
        `admin/proceso/solicitud/${solicitudId}`,
        null,
        (respuesta) => {
          setSolicitud(respuesta);
          document.title = `Solicitud ${respuesta.nombrefantasia}`;
        },
        (respuesta) => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: respuesta[0].msg,
            sticky: true
          });
        }
      );
    }
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando carga el solicitud apaga el loader
  useEffect(() => {
    if (solicitud) {
      setLoader(false);
    }
  }, [solicitud]);

  const marcarSolicitudComoProcesada = () => {
    setSolicitud((prevSolicitud) => ({
      ...prevSolicitud,
      procesada: true
    }));
  };

  const marcarSolicitudComoNoProcesada = () => {
    setSolicitud((prevSolicitud) => ({
      ...prevSolicitud,
      procesada: null
    }));
  };

  // sinPropuesta cierra la solicitud sin generar la propuesta comercial
  function cerrarSolicitud(sinPropuesta = false) {
    setLoaderCerrar(true);
    accessAPI(
      "PUT",
      `admin/proceso/solicitud/${solicitudId}`,
      sinPropuesta ? { sinPropuesta: true } : null,
      () => {
        setLoaderCerrar(false);
        marcarSolicitudComoProcesada();
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cerrar la solicitud",
          sticky: true
        });
        setLoaderCerrar(false);
      }
    );
  }

  function reabrirSolicitud() {
    setLoaderReabrir(true);
    accessAPI(
      "PUT",
      `admin/proceso/solicitud/${solicitudId}/reabrir`,
      null,
      () => {
        setLoaderReabrir(false);
        marcarSolicitudComoNoProcesada();
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al reabrir la solicitud",
          sticky: true
        });
        setLoaderReabrir(false);
      }
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      {loader && (
        <div>
          <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1em", marginBottom: "1em" }}>
            <Skeleton width="18rem" height="2rem" />
            <Skeleton width="10rem" height="2.5rem" />
          </div>
          <div className="grid">
            <div className="col-12 lg:col-6">
              <Card>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} css={{ marginBottom: "0.8em" }}>
                    <Skeleton width="6rem" height="0.9rem" css={{ marginBottom: "0.3em" }} />
                    <Skeleton width="14rem" height="1.1rem" />
                  </div>
                ))}
              </Card>
            </div>
            <div className="col-12 lg:col-6">
              <Card>
                <Skeleton width="12rem" height="1.3rem" css={{ marginBottom: "1em" }} />
                {[1, 2].map((i) => (
                  <Skeleton key={i} width="100%" height="2.5rem" css={{ marginBottom: "0.5em" }} />
                ))}
              </Card>
            </div>
          </div>
        </div>
      )}
      {!loader && (
        <>
          <Titulo
            urlVolver={`/clientes/${solicitud.cliente.id}/procesos`}
            etiqueta={solicitud.procesada ? "Cerrada" : null}
            botones={
              solicitud.procesada ? (
                <Boton
                  transparente
                  callback={reabrirSolicitud}
                  confirmacion="¿Está seguro?"
                  loader={loaderReabrir}
                >
                  Reabrir solicitud
                </Boton>
              ) : (
                <Boton
                  rojo
                  transparente
                  callback={() => cerrarSolicitud()}
                  confirmacion="¿Está seguro?"
                  loader={loaderCerrar}
                  menu={[
                    {
                      etiqueta: "Cerrar sin propuesta",
                      callback: () => cerrarSolicitud(true)
                    }
                  ]}
                >
                  Cerrar solicitud
                </Boton>
              )
            }
          >
            Solicitud - {solicitud.nombrefantasia}
          </Titulo>
          <div className="grid">
            <div className="col-12 lg:col-6">
              <TarjetaDetallesSolicitud solicitud={solicitud} />
            </div>
            <div
              className="col-12 lg:col-6"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              <TarjetaProcesosVigentes
                solicitud={solicitud}
                onMarcarProcesada={marcarSolicitudComoProcesada}
                onProcesoCreado={() => setRecargaEvaluacion((n) => n + 1)}
              />
              <TarjetaEvaluacionSolicitud
                solicitud={solicitud}
                recarga={recargaEvaluacion}
                toast={toast}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}
